import styled from "styled-components";
import dayjs from "dayjs";

import { Grid } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";

import { useAuth } from "src/hooks/useAuth";
import { useDialog } from "src/context/DialogContext";
import { convertIntToCurrency } from "src/common";

import Content from "src/components/ui/Content";
import Panel from "src/components/ui/Panel";
import Button from "src/components/core/Button";

import {
  Title,
  Desc,
  Line,
  Section,
  Topic,
} from "src/modules/order/shipping-order/components";
import CustomerInfo from "src/modules/order/shipping-order/status-order/CustomerInfo";
import PurchaseInfo from "src/modules/order/shipping-order/status-order/PurchaseInfo";
import DocumentInfo from "src/modules/order/shipping-order/status-order/Document";
import Note from "src/modules/order/shipping-order/status-order/Note";
import DeliveryProofImgDialog from "src/modules/order/shipping-order/modal/DeliveryProofImgDialog";
import ShippingProofImgDialog from "src/modules/order/shipping-order/modal/ShippingProofImgDialog";
import PreviewImageList from "src/modules/order/shipping-order/modal/PreviewImageList";

const TextSubTable = styled.p`
  color: #6c6c70;
  font-size: 0.75rem;
  font-weight: 400;
  margin: 2px 0;
`;

const Price = styled.p`
  font-size: 1.5rem;
  color: #e13b30;
  font-weight: 600;
  margin: 0 5px 0 0;
`;

const ShippingDetail = ({ info, setIsLoading, callBackFetchInfo }) => {
  const { checkAllowPermission } = useAuth();
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const isJSONString = (str) => {
    if (typeof str !== "string") return str;
    try {
      return isJSONString(str);
    } catch {
      return str;
    }
  };

  return (
    <Grid container xs={12}>
      <Grid container xs={12} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={6}>
          <Title>รายละเอียดการจัดส่งสินค้า</Title>
        </Grid>
      </Grid>

      <Section
        borderBottom={
          isJSONString(info?.method) !== "ลูกค้ารับเอง" &&
          !!info?.payment?.info?.bankNo
        }
      >
        <Grid item xs={6} pr={1.5}>
          <Grid item xs={12} mb={0.5}>
            <Topic>การจัดส่ง</Topic>
          </Grid>

          <Grid item xs={12}>
            <Desc>
              {info?.status?.value === "failed"
                ? "สินค้าไม่พร้อมจัดส่ง"
                : "มีสินค้าพร้อมจัดส่ง"}
            </Desc>
          </Grid>
        </Grid>

        <Grid item xs={6} pl={1.5}>
          <Grid item xs={12} mb={0.5}>
            <Topic>รูปแบบการจัดส่ง</Topic>
          </Grid>

          <Grid item xs={12}>
            <Desc>{isJSONString(info?.method)}</Desc>
          </Grid>
        </Grid>

        {info?.fulfillmentProcess.length > 1 ? (
          <>
            <Grid conaiter xs={12} mt={2}>
              <Topic primary>การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง)</Topic>
            </Grid>

            <Grid item xs={6} mt={2} pr={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ขนส่งที่ใช้ในการจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Desc>
                    {info?.fulfillmentProcess[0]?.carrier === "อื่นๆ"
                      ? info?.fulfillmentProcess[0]?.carrierAdditional
                      : info?.fulfillmentProcess[0]?.carrier}
                  </Desc>
                </Grid>
                {info?.fulfillmentProcess[0]?.carrier !==
                  "จัดส่งโดยร้านค้า" && (
                  <Grid item xs={12}>
                    <Desc>
                      {info?.fulfillmentProcess[0]?.carrier === "Lalamove"
                        ? info?.fulfillmentProcess[0]?.trackingUrl
                        : info?.fulfillmentProcess[0]?.trackingNumber}
                    </Desc>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={6} mt={2} pl={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ค่าจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Desc>
                  {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                    ? "มีค่าจัดส่ง"
                    : info?.fulfillmentProcess[0]?.type === "ไม่มีค่าจัดส่ง"
                      ? "ไม่มีค่าจัดส่ง"
                      : "เก็บเงินปลายทาง"}
                </Desc>
              </Grid>
            </Grid>

            <Grid conaiter xs={12} mt={2}>
              <Topic primary>การส่งของที่ 2 (ขนส่งส่งไปลูกค้า)</Topic>
            </Grid>

            <Grid item xs={6} mt={2} pr={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ขนส่งที่ใช้ในการจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Desc>
                    {info?.fulfillmentProcess[1]?.carrier === "อื่นๆ"
                      ? info?.fulfillmentProcess[1]?.carrierAdditional
                      : info?.fulfillmentProcess[1]?.carrier}
                  </Desc>
                </Grid>
                {info?.fulfillmentProcess[1]?.carrier !==
                  "จัดส่งโดยร้านค้า" && (
                  <Grid item xs={12}>
                    <Desc>
                      {info?.fulfillmentProcess[1]?.carrier === "Lalamove"
                        ? info?.fulfillmentProcess[1]?.trackingUrl
                        : info?.fulfillmentProcess[1]?.trackingNumber}
                    </Desc>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={6} mt={2} pl={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ค่าจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Desc>
                  {info?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง"
                    ? "มีค่าจัดส่ง"
                    : info?.fulfillmentProcess[1]?.type === "ไม่มีค่าจัดส่ง"
                      ? "ไม่มีค่าจัดส่ง"
                      : "เก็บเงินปลายทาง"}
                </Desc>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6} mt={2} pr={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ขนส่งที่ใช้ในการจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Desc>
                    {info?.fulfillmentProcess[0]?.carrier === "อื่นๆ"
                      ? info?.fulfillmentProcess[0]?.carrierAdditional
                      : info?.fulfillmentProcess[0]?.carrier}
                  </Desc>
                </Grid>

                {info?.fulfillmentProcess[1]?.carrier !==
                  "จัดส่งโดยร้านค้า" && (
                  <Grid item xs={12}>
                    <Desc>
                      {info?.fulfillmentProcess[0]?.carrier === "Lalamove" ||
                      info?.fulfillmentProcess[0]?.carrier === "Lalamove ส่งฟรี"
                        ? info?.fulfillmentProcess[0]?.trackingUrl
                        : info?.fulfillmentProcess[0]?.trackingNumber || ""}
                    </Desc>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={6} mt={2} pl={1.5}>
              <Grid item xs={12} mb={0.5}>
                <Topic>ค่าจัดส่ง</Topic>
              </Grid>

              <Grid item xs={12}>
                <Desc>
                  {/* {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                    ? "มีค่าจัดส่ง"
                    : info?.fulfillmentProcess[0]?.type === "ไม่มีค่าจัดส่ง"
                      ? "ไม่มีค่าจัดส่ง"
                      : "เก็บเงินปลายทาง"} */}
                  {info?.fulfillmentProcess[0]?.type}
                </Desc>
              </Grid>
            </Grid>
          </>
        )}

        <Grid item xs={6} mt={2}>
          <Grid item xs={12} pr={2}>
            <Topic>วันที่จัดส่ง</Topic>
          </Grid>
          <Grid item xs={12}>
            <Desc>
              {dayjs(
                info?.fulfillmentProcess?.[0]?.deliveryProofs?.[0]
                  ?.deliveryProof?.createdAt,
              ).format("DD-MM-YYYY")}
            </Desc>
          </Grid>
        </Grid>

        <Grid item xs={6} mt={2} pl={1.5}>
          <Grid item xs={12} pr={2}>
            <Topic>วันที่จัดส่งสำเร็จ</Topic>
          </Grid>
          <Grid item xs={12}>
            <Desc>
              {dayjs(info?.fulfillmentProcess[0]?.shippedDate).format(
                "DD-MM-YYYY",
              )}
            </Desc>
          </Grid>
        </Grid>

        <Grid container xs={12} sm={6} mt={2} pr={{ xs: 0, sm: 2 }}>
          <Grid item xs={12}>
            <Topic>รูปแพ็คสินค้าก่อนจัดส่ง</Topic>
          </Grid>

          {!!info?.fulfillmentProcess?.[0]?.deliveryProofs &&
          info?.fulfillmentProcess?.[0]?.deliveryProofs?.length > 0 ? (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<VisibilityIcon />}
                fullWidth
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <PreviewImageList
                        onClose={() => closeDialog()}
                        title="รูปสินค้าที่จัดส่ง"
                        imageList={info.fulfillmentProcess?.[0]?.deliveryProofs}
                        keyImg={"deliveryProof"}
                      />
                    ),
                  });
                }}
              >
                ดูรูปสินค้าที่จัดส่ง
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                disabled={
                  !checkAllowPermission("orders.can_update_shippingOrder")
                }
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <DeliveryProofImgDialog
                        orderDetail={info}
                        onClose={() => closeDialog()}
                        onSuccess={() => {
                          closeAllDialogs();
                          callBackFetchInfo();
                        }}
                        setIsLoading={setIsLoading}
                      />
                    ),
                  });
                }}
                startIcon={<AddIcon />}
                sx={{ width: "100%" }}
              >
                เพิ่มรูปสินค้าที่จัดส่ง
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid container xs={12} sm={6} mt={2} pl={{ xs: 0, sm: 1.5 }}>
          <Grid item xs={12}>
            <Topic>รูปที่ส่งให้ลูกค้า</Topic>
          </Grid>

          {!!info?.fulfillmentProcess?.[0]?.shippingProofs &&
          info?.fulfillmentProcess?.[0]?.shippingProofs?.length > 0 ? (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<VisibilityIcon />}
                fullWidth
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <PreviewImageList
                        onClose={() => closeDialog()}
                        title="รูปที่ส่งให้ลูกค้า"
                        imageList={info.fulfillmentProcess?.[0]?.shippingProofs}
                        keyImg={"shippingProof"}
                      />
                    ),
                  });
                }}
              >
                ดูรูปที่ส่งให้ลูกค้า
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                disabled={
                  !checkAllowPermission("orders.can_update_shippingOrder")
                }
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <ShippingProofImgDialog
                        orderDetail={info}
                        onClose={() => closeDialog()}
                        onSuccess={() => {
                          closeAllDialogs();
                          callBackFetchInfo();
                        }}
                        setIsLoading={setIsLoading}
                      />
                    ),
                  });
                }}
                startIcon={<AddIcon />}
                sx={{ width: "100%" }}
              >
                เพิ่มรูปที่ส่งให้ลูกค้า
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid item xs={6} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Topic>หมายเหตุ</Topic>
          </Grid>
          <Grid item xs={12}>
            <Desc>{info?.fulfillmentProcess[0]?.remark ?? "-"}</Desc>
          </Grid>
        </Grid>
      </Section>

      {isJSONString(info?.method) !== "ลูกค้ารับเอง" &&
        !!info?.payment?.info?.bankNo && (
          <>
            <Grid container xs={12} sx={{ mt: { xs: 2, md: 3 } }} mb={1}>
              <Title>ช่องทางชำระเงิน</Title>
            </Grid>

            <Section borderBottom>
              <Grid item xs={6} sx={{ mt: { xs: 1, md: 2 } }}>
                <Grid item xs={12} pr={2}>
                  <Topic>ชื่อบัญชี</Topic>
                </Grid>
                <Grid item xs={12} display="flex">
                  <Desc>{info?.payment?.info?.bankAccountName}</Desc>
                </Grid>
              </Grid>

              <Grid item xs={6} sx={{ mt: { xs: 1, md: 2 } }} pl={1.5}>
                <Grid item xs={12} pr={2}>
                  <Topic>เลขที่บัญชี</Topic>
                </Grid>
                <Grid item xs={12} display="flex">
                  <Desc>{info?.payment?.info?.bankNo}</Desc>
                </Grid>
              </Grid>
            </Section>

            <Section borderBottom>
              <Grid container xs={12}>
                <Grid item xs={12} sx={{ mt: { xs: 2, md: 3 } }}>
                  <Title>ค่าจัดส่งเพิ่มเติม</Title>
                </Grid>

                {info?.fulfillmentProcess.length > 1 ? (
                  <>
                    {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" && (
                      <Grid container xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
                        <Grid item xs={9}>
                          <Desc>
                            ค่าจัดส่ง (การส่งของที่ 1 (ร้านค้าส่งไปขนส่ง))
                          </Desc>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} บาท`}</Desc>
                        </Grid>
                      </Grid>
                    )}

                    {info?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง" ||
                    (info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                      info?.fulfillmentProcess[1]?.type ===
                        "เก็บเงินปลายทาง") ? (
                      <Grid container xs={12} sx={{ mt: { xs: 1, md: 1 } }}>
                        <Grid item xs={9}>
                          <Desc>
                            ค่าจัดส่ง (การส่งของที่ 2 (ขนส่งส่งไปลูกค้า))
                          </Desc>
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[1]?.shippingFee)} บาท`}</Desc>
                        </Grid>
                      </Grid>
                    ) : null}

                    <Grid container xs={12} sx={{ mt: { xs: 1, md: 2 } }}>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        alignItems="flex-end"
                        sx={{ mb: { xs: 0, sm: 2 } }}
                      >
                        <Title>ยอดชำระ</Title>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                        sx={{ alignItems: { xs: "baseline" } }}
                      >
                        {(info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                          info?.fulfillmentProcess[1]?.type ===
                            "มีค่าจัดส่ง") ||
                        (info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                          info?.fulfillmentProcess[1]?.type ===
                            "เก็บเงินปลายทาง") ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee + info?.fulfillmentProcess[1]?.shippingFee)} `}
                          </Price>
                        ) : info?.fulfillmentProcess[0]?.type ===
                          "มีค่าจัดส่ง" ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} `}
                          </Price>
                        ) : info?.fulfillmentProcess[1]?.type ===
                          "มีค่าจัดส่ง" ? (
                          <Price>
                            {`${convertIntToCurrency(info?.fulfillmentProcess[1]?.shippingFee)} `}
                          </Price>
                        ) : null}

                        <Desc>บาท</Desc>
                      </Grid>
                    </Grid>

                    {info?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                      info?.fulfillmentProcess[1]?.type ===
                        "เก็บเงินปลายทาง" && (
                        <>
                          <Line />

                          <Grid container xs={12} sx={{ mt: { xs: 2, md: 0 } }}>
                            <Grid
                              item
                              xs={8}
                              display="flex"
                              justifyItems="flex-end"
                            >
                              <Title>
                                ค่าจัดส่งโดยประมาณ (เก็บเงินปลายทาง)
                              </Title>
                            </Grid>

                            <Grid
                              item
                              xs={4}
                              display="flex"
                              justifyContent="flex-end"
                            >
                              <Price>
                                {convertIntToCurrency(
                                  info?.fulfillmentProcess[1]?.shippingFee,
                                )}
                              </Price>

                              <Desc>บาท</Desc>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <TextSubTable>
                              *เพื่อแจ้งให้ลูกค้าเตรียมเงินและทราบค่าจัดส่งโดยประมาณ
                            </TextSubTable>
                          </Grid>
                        </>
                      )}
                  </>
                ) : (
                  <>
                    <Grid container xs={12} sx={{ mt: { xs: 1, md: 3 } }}>
                      <Grid item xs={6}>
                        <Desc>ค่าจัดส่ง</Desc>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Desc>{`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} บาท`}</Desc>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      xs={12}
                      sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 2, sm: 3 } }}
                    >
                      <Grid item xs={6} display="flex" alignItems="flex-end">
                        <Title>ยอดชำระ</Title>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Price>
                          {`${convertIntToCurrency(info?.fulfillmentProcess[0]?.shippingFee)} `}
                        </Price>

                        <Desc>บาท</Desc>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Section>

            <Grid container xs={12}>
              <Grid
                item
                xs={12}
                sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 1, sm: 0 } }}
                display="flex"
                justifyContent="space-between"
                alignContent="center"
              >
                <Title>การชำระเงิน</Title>
                <Button
                  variant="outlined"
                  className="MuiButton-file"
                  startIcon={<VisibilityIcon></VisibilityIcon>}
                  onClick={() =>
                    window.open(info?.payment?.paymentProof.docUrl)
                  }
                >
                  ดูหลักฐานการชำระเงิน
                </Button>
              </Grid>

              <Grid container xs={12} sx={{ mt: { xs: 0, md: 2 } }}>
                <Grid item xs={6}>
                  <Desc>วันที่ชำระเงิน</Desc>
                </Grid>

                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <Desc>
                    {dayjs(info?.payment?.paymentDate).format(
                      "DD-MM-YYYY HH:mm",
                    )}
                  </Desc>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
    </Grid>
  );
};

const Shipped = ({ info, callbackCloseDocument, setIsLoading }) => {
  return (
    <>
      <Content>
        <Panel>
          <CustomerInfo info={info} />
        </Panel>

        <Panel>
          <PurchaseInfo info={info.order} />
        </Panel>

        <Panel>
          <ShippingDetail
            info={info}
            callBackFetchInfo={callbackCloseDocument}
            setIsLoading={setIsLoading}
          />
        </Panel>

        <Panel>
          <DocumentInfo
            info={info}
            isCloseDialog={callbackCloseDocument}
            setIsLoading={setIsLoading}
          />
        </Panel>

        <Panel>
          <Note info={info?.order} />
        </Panel>
      </Content>
    </>
  );
};

export default Shipped;
